import { useState, useMemo, useEffect, type ComponentType, type SVGProps } from 'react';
import { api } from '@/api/api-client';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Bell, Info, Gift, Clock, RefreshCw, CheckCheck, Calendar, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { currentDateFnsLocale, currentLanguage } from '@/i18n/locale';
import { hasMeetingBooked } from '@/utils/callUtils';

type IconType = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

type NotifType = 'info' | 'promo' | 'reminder' | 'update';

interface TypeMeta {
  icon: IconType;
  color: string;
}

const typeConfig: Record<NotifType, TypeMeta> = {
  info: { icon: Info, color: 'bg-blue-100 text-blue-600' },
  promo: { icon: Gift, color: 'bg-purple-100 text-purple-600' },
  reminder: { icon: Clock, color: 'bg-orange-100 text-orange-600' },
  update: { icon: RefreshCw, color: 'bg-green-100 text-green-600' },
};

interface Notification {
  id: string;
  type?: NotifType | string | null;
  title?: string | null;
  message?: string | null;
  is_read?: boolean;
  created_date?: string | null;
}

interface CallRow {
  call_outcome?: string | null;
  meeting_booked?: boolean | null;
  connected_at?: string | null;
  started_at?: string | null;
  created_date?: string | null;
}

interface NotifApi {
  filter(query: Record<string, unknown>, sort: string): Promise<Notification[]>;
  update(id: string, patch: Record<string, unknown>): Promise<unknown>;
}

interface CallApi {
  filter(query: Record<string, unknown>, sort: string, limit: number): Promise<CallRow[]>;
}

interface ApiShape {
  entities: {
    ClientNotification: NotifApi;
    ParallelCall: CallApi;
  };
}

interface NotificationsDropdownProps {
  clientEmail?: string;
}

export default function NotificationsDropdown({ clientEmail }: NotificationsDropdownProps) {
  const { t } = useTranslation(['admin']);
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: notifications = [] } = useQuery<Notification[]>({
    queryKey: ['client-notifications', clientEmail],
    queryFn: () =>
      (api as unknown as ApiShape).entities.ClientNotification.filter(
        {},
        '-created_date',
      ),
    enabled: !!clientEmail,
    refetchInterval: 5000,
  });

  const { data: recentCalls = [] } = useQuery<CallRow[]>({
    queryKey: ['notif-digest', clientEmail],
    queryFn: () =>
      (api as unknown as ApiShape).entities.ParallelCall.filter(
        {},
        '-started_at',
        200,
      ),
    enabled: !!clientEmail,
    staleTime: 10000,
  });

  const digest = useMemo(() => {
    const since = new Date();
    since.setDate(since.getDate() - 30);
    const toDate = (c: CallRow) =>
      new Date(c.connected_at || c.started_at || c.created_date || 0);
    const calls = recentCalls.filter((c) => toDate(c) >= since);
    return {
      meetings: calls.filter(hasMeetingBooked).length,
      callbacks: calls.filter((c) => c.call_outcome === 'callback_requested').length,
      unqualified: calls.filter((c) => !c.call_outcome).length,
    };
  }, [recentCalls]);

  const markAsRead = useMutation({
    mutationFn: async (id: string) => {
      await (api as unknown as ApiShape).entities.ClientNotification.update(id, {
        is_read: true,
        read_at: new Date().toISOString(),
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['client-notifications', clientEmail] }),
  });

  const markAllAsRead = useMutation({
    mutationFn: async () => {
      const unread = notifications.filter((n) => !n.is_read);
      for (const notif of unread) {
        await (api as unknown as ApiShape).entities.ClientNotification.update(notif.id, {
          is_read: true,
          read_date: new Date().toISOString(),
        });
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['client-notifications', clientEmail] }),
  });

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  useEffect(() => {
    if (open && unreadCount > 0) {
      markAllAsRead.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, unreadCount]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-11 w-11"
          aria-label={unreadCount > 0
            ? t('admin:backoffice.notificationsDropdown.openUnread', { count: unreadCount })
            : t('admin:backoffice.notificationsDropdown.open')}
        >
          <Bell className="w-5 h-5 text-gray-500" aria-hidden />
          {unreadCount > 0 && (
            <span aria-hidden className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white font-bold flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      {/* REV-W5-7: never wider than the screen (390 px), 24 rem from sm up. */}
      <PopoverContent align="end" className="w-[calc(100vw-2rem)] p-0 sm:w-96">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">{t('admin:backoffice.notificationsDropdown.title')}</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-purple-600 hover:text-purple-700"
              onClick={() => markAllAsRead.mutate()}
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              {t('admin:backoffice.notificationsDropdown.markAllRead')}
            </Button>
          )}
        </div>

        <div className="p-3 border-b bg-gray-50/60">
          <div className="grid grid-cols-3 gap-2">
            <div className="flex items-center gap-2 rounded-md bg-white border p-2">
              <div className="w-6 h-6 rounded bg-green-100 flex items-center justify-center">
                <Calendar className="w-3.5 h-3.5 text-green-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">{t('admin:backoffice.notificationsDropdown.newMeeting')}</div>
                <div className="text-sm font-semibold">{digest.meetings || 0}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-md bg-white border p-2">
              <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center">
                <Clock className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">{t('admin:backoffice.notificationsDropdown.callbackRequested')}</div>
                <div className="text-sm font-semibold">{digest.callbacks || 0}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-md bg-white border p-2">
              <div className="w-6 h-6 rounded bg-orange-100 flex items-center justify-center">
                <AlertCircle className="w-3.5 h-3.5 text-orange-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">{t('admin:backoffice.notificationsDropdown.notQualified')}</div>
                <div className="text-sm font-semibold">{digest.unqualified || 0}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{t('admin:backoffice.notificationsDropdown.empty')}</p>
            </div>
          ) : (
            <AnimatePresence>
              {notifications.slice(0, 10).map((notif) => {
                const key = (notif.type as NotifType) ?? 'info';
                const config = typeConfig[key] ?? typeConfig.info;
                const Icon = config.icon;
                return (
                  <motion.div
                    key={notif.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={`p-3 border-b last:border-0 hover:bg-gray-50 cursor-pointer transition-colors ${
                      !notif.is_read ? 'bg-purple-50/50' : ''
                    }`}
                    onClick={() => !notif.is_read && markAsRead.mutate(notif.id)}
                  >
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-lg ${config.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${!notif.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                            {notif.title}
                          </p>
                          {!notif.is_read && (
                            <span className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{notif.message}</p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {notif.created_date && !isNaN(new Date(notif.created_date).getTime())
                            // i18n-exempt: date-fns patterns, one per language
                            ? format(new Date(notif.created_date), currentLanguage() === 'en' ? "dd MMM 'at' HH:mm" : 'dd MMM à HH:mm', {
                                locale: currentDateFnsLocale(),
                              })
                            : '—'}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
